interface AppConfig {
  apiUrl: string;
  oauthProvider: string;
  environment: string;
  darkModeEnabled?: boolean;
}

let config: AppConfig | null = null;

/**
 * Loads the application configuration from the public/config.json file
 * This allows runtime configuration without rebuilding the application
 */
export async function loadConfig(): Promise<AppConfig> {
  if (config) {
    return config;
  }

  try {
    // Check for environment variables first (works in both dev and prod)
    // Vite replaces these at build time for production
    if (import.meta.env.VITE_API_URL) {
      config = {
        apiUrl: import.meta.env.VITE_API_URL,
        oauthProvider: import.meta.env.VITE_OAUTH_PROVIDER || 'authentik',
        environment: import.meta.env.VITE_ENVIRONMENT || (import.meta.env.PROD ? 'production' : 'development'),
        darkModeEnabled: import.meta.env.VITE_DARK_MODE === 'true' || false,
      };
      console.info('Configuration loaded from environment variables:', {
        apiUrl: config.apiUrl,
        oauthProvider: config.oauthProvider,
        environment: config.environment,
      });
      return config;
    }

    // Fall back to config.json (for runtime configuration without rebuild)
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config.json: ${response.statusText}`);
    }

    const loadedConfig = await response.json();

    // Validate required fields
    if (!loadedConfig.apiUrl) {
      throw new Error('Missing required field: apiUrl in config.json');
    }

    config = {
      apiUrl: loadedConfig.apiUrl,
      oauthProvider: loadedConfig.oauthProvider || 'authentik',
      environment: loadedConfig.environment || (import.meta.env.PROD ? 'production' : 'development'),
      darkModeEnabled: loadedConfig.darkModeEnabled || false,
    };

    console.info('Configuration loaded from config.json:', {
      apiUrl: config.apiUrl,
      oauthProvider: config.oauthProvider,
      environment: config.environment,
    });

    return config;
  } catch (error) {
    console.error('Failed to load configuration:', error);
    throw error;
  }
}

/**
 * Gets the current configuration
 * Throws an error if configuration hasn't been loaded yet
 */
export function getConfig(): AppConfig {
  if (!config) {
    throw new Error('Configuration not loaded. Call loadConfig() first.');
  }
  return config;
}

/**
 * Gets the API base URL
 */
export function getApiUrl(): string {
  return getConfig().apiUrl;
}

/**
 * Gets the OAuth provider
 */
export function getOAuthProvider(): string {
  return getConfig().oauthProvider;
}

/**
 * Gets the environment
 */
export function getEnvironment(): string {
  return getConfig().environment;
}

/**
 * Checks if running in development mode
 */
export function isDevelopment(): boolean {
  return getConfig().environment === 'development';
}

/**
 * Checks if running in production mode
 */
export function isProduction(): boolean {
  return getConfig().environment === 'production';
}

/**
 * Checks if dark mode is enabled
 */
export function isDarkModeEnabled(): boolean {
  return getConfig().darkModeEnabled || false;
}

